import { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';
import {
  MapPin,
  Clock,
  Gauge,
  Heart,
  HeartPulse,
  Layers,
  Zap,
  ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import RunningWorkoutViz from '../training/RunningWorkoutViz';

function formatDistanceKm(meters) {
  const m = parseFloat(meters);
  if (!Number.isFinite(m) || m <= 0) return '—';
  return `${(m / 1000).toFixed(2)} km`;
}

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatPace(speed) {
  const sp = parseFloat(speed);
  if (!Number.isFinite(sp) || sp === 0) return '—';
  const paceSeconds = 1000 / sp;
  if (!Number.isFinite(paceSeconds)) return '—';
  const mins = Math.floor(paceSeconds / 60);
  const secs = Math.round(paceSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')} /km`;
}

function paceFromDistTime(distM, timeSec) {
  if (!distM || !timeSec || distM <= 0 || timeSec <= 0) return '—';
  const speed = distM / timeSec;
  return formatPace(speed);
}

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-lg border border-border bg-card p-3 text-center">
    <div className="mb-1.5 flex justify-center text-primary [&>svg]:h-4 [&>svg]:w-4">{icon}</div>
    <p className="text-lg font-bold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

export default function DayCombinedSummaryModal({ open, onClose, dayKey, dayData, onActivityPeek }) {
  const { t, language } = useLanguage();
  const locale = language === 'no' ? nb : enUS;

  const dateLabel = useMemo(() => {
    if (!dayKey) return '';
    try {
      return format(parseISO(dayKey), 'EEEE d. MMMM yyyy', { locale });
    } catch {
      return dayKey;
    }
  }, [dayKey, locale]);

  const activities = dayData?.stravaActivities || [];
  const totalDistM = dayData?.totalDistanceM || 0;
  const totalMovingSec = dayData?.totalMovingSec || 0;

  const totalSuffer = useMemo(() => {
    let sum = 0;
    let hasAny = false;
    for (const a of activities) {
      if (a.sufferScore != null && Number.isFinite(a.sufferScore)) {
        sum += a.sufferScore;
        hasAny = true;
      }
    }
    return hasAny ? sum : null;
  }, [activities]);

  const weightedAvgHr = useMemo(() => {
    let hrTimeSum = 0;
    let timeSum = 0;
    for (const a of activities) {
      if (a.averageHeartrate && a.movingTimeSec > 0) {
        hrTimeSum += a.averageHeartrate * a.movingTimeSec;
        timeSum += a.movingTimeSec;
      }
    }
    return timeSum > 0 ? Math.round(hrTimeSum / timeSum) : null;
  }, [activities]);

  if (!open || !dayData) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        showCloseButton
        className="z-[10250] flex max-h-[80vh] w-[90vw] max-w-[60vw] flex-col gap-0 overflow-hidden p-0"
        overlayClassName="z-[10240]"
      >
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <DialogTitle className="text-left text-base font-semibold">
            {t('trainingLog.plannerCombinedDayTitle', { date: dateLabel })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          {/* Stat cards */}
          <div className="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
            <StatCard
              icon={<Layers />}
              label={t('trainingLog.plannerCombinedSessions')}
              value={String(activities.length)}
            />
            <StatCard
              icon={<MapPin />}
              label={t('trainingLog.plannerCombinedTotalDistance')}
              value={formatDistanceKm(totalDistM)}
            />
            <StatCard
              icon={<Clock />}
              label={t('trainingLog.plannerCombinedTotalDuration')}
              value={formatDuration(totalMovingSec)}
            />
            <StatCard
              icon={<Gauge />}
              label={t('trainingLog.plannerCombinedAvgPace')}
              value={paceFromDistTime(totalDistM, totalMovingSec)}
            />
            {weightedAvgHr != null && (
              <StatCard
                icon={<Heart />}
                label={t('trainingLog.plannerCombinedAvgHr')}
                value={`${weightedAvgHr} bpm`}
              />
            )}
            {totalSuffer != null && (
              <StatCard
                icon={<HeartPulse />}
                label={t('trainingLog.plannerCombinedTotalSuffer')}
                value={String(totalSuffer)}
              />
            )}
          </div>

          {/* Per-session table */}
          <div className="min-h-0 overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[140px]">{t('trainingLog.plannerCombinedName')}</TableHead>
                  <TableHead className="text-right">{t('trainingLog.plannerCombinedDuration')}</TableHead>
                  <TableHead className="text-right">{t('trainingLog.plannerCombinedDistance')}</TableHead>
                  <TableHead className="text-right">{t('trainingLog.plannerCombinedPace')}</TableHead>
                  <TableHead className="text-right">{t('trainingLog.plannerCombinedHr')}</TableHead>
                  <TableHead className="text-right">{t('trainingLog.plannerCombinedPower')}</TableHead>
                  <TableHead className="text-right">{t('trainingLog.plannerCombinedSuffer')}</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {activities.map((a) => {
                  const clickable = typeof onActivityPeek === 'function';
                  return (
                    <TableRow
                      key={a.id}
                      className={cn(clickable && 'cursor-pointer hover:bg-accent/50')}
                      onClick={clickable ? () => onActivityPeek(a.id) : undefined}
                      title={clickable ? t('trainingLog.plannerCombinedClickRow') : undefined}
                    >
                      <TableCell className="font-medium">
                        <div className="flex flex-col gap-1">
                          <span className="truncate">{a.name || '—'}</span>
                          {Array.isArray(a.previewSplits) && a.previewSplits.length > 0 && (
                            <RunningWorkoutViz splits={a.previewSplits} heightPx={14} className="max-w-[180px]" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatDuration(a.movingTimeSec)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatDistanceKm(a.distanceM)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.averageSpeed ? formatPace(a.averageSpeed) : paceFromDistTime(a.distanceM, a.movingTimeSec)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.averageHeartrate ? `${a.averageHeartrate}` : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.averagePower ? (
                          <span className="inline-flex items-center gap-0.5">
                            <Zap className="h-3 w-3 text-muted-foreground" />
                            {a.averagePower}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.sufferScore != null && Number.isFinite(a.sufferScore) ? a.sufferScore : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {clickable && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
